// CELPIP Speaking exam screen: instruction interstitial -> preparation
// countdown -> recording countdown (auto-stop) -> submitted state. Uses
// MediaRecorder; recording starts and stops on the exam's clock, exactly once,
// like the real test.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mic } from 'lucide-react';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import ContentImage from '@/components/ContentImage';
import { acquireMicStream, buildLevelMeter, releaseStream as releaseMicStream } from '@/lib/micRecorder';
import { cn } from '@/lib/utils';

// `submit()` always navigates to the review page, so a PHASE.DONE render was
// never reachable. The block, its result/marking state, and their imports
// were dead weight; deleted to keep the runner honest about what it does.
const PHASE = { INTRO: 'INTRO', PREP: 'PREP', RECORD: 'RECORD', SUBMITTING: 'SUBMITTING', ERROR: 'ERROR' };

// The picture for Task 3/4/8. The photo is generated at deploy and dropped at
// media/images/<image_ref>; until then a clean framed placeholder reserves the
// slot and the scene description (as the caption) keeps the task doable. The
// real photo drops straight in once it exists.
function SceneImage({ imageRef, scene }) {
  return <ContentImage imageRef={imageRef} caption={scene} className="m-4" />;
}

const TASK_META = {
  speaking_t1_advice: { title: 'Task 1: Giving Advice' },
  speaking_t2_experience: { title: 'Task 2: Talking about a Personal Experience' },
  speaking_t3_scene: { title: 'Task 3: Describing a Scene' },
  speaking_t4_predictions: { title: 'Task 4: Making Predictions' },
  speaking_t5_compare: { title: 'Task 5: Comparing and Persuading' },
  speaking_t6_difficult: { title: 'Task 6: Dealing with a Difficult Situation' },
  speaking_t7_opinion: { title: 'Task 7: Expressing Opinions' },
  speaking_t8_unusual: { title: 'Task 8: Describing an Unusual Situation' },
};

export default function SpeakingRunner() {
  const { exam, taskType } = useParams();
  const [searchParams] = useSearchParams();
  const setParam = searchParams.get('set');
  const navigate = useNavigate();
  const meta = TASK_META[taskType] || { title: 'Speaking task' };

  const [item, setItem] = useState(null);
  const [phase, setPhase] = useState(PHASE.INTRO);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [micError, setMicError] = useState('');
  const [micBusy, setMicBusy] = useState(false);
  const [level, setLevel] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedRef = useRef(null);
  const phaseRef = useRef(PHASE.INTRO);
  const streamRef = useRef(null);
  const mimeRef = useRef({ mime: '', ext: 'webm' });
  const meterRef = useRef(null);
  const levelRafRef = useRef(0);

  const load = useCallback(async () => {
    setError('');
    setPhase(PHASE.INTRO);
    phaseRef.current = PHASE.INTRO;
    try {
      const res = await api.practiceSets(exam, taskType, 1, setParam);
      // One-shot: if this set was already attempted, jump to read-only review.
      if (res.data.review_submission_id) {
        navigate(`/${exam}/submissions/${res.data.review_submission_id}/review`, { replace: true });
        return;
      }
      setItem(res.data.sets[0]);
    } catch (err) {
      setError(err.response?.data?.detail || 'No speaking prompt available.');
      setPhase(PHASE.ERROR);
    }
  }, [exam, taskType, setParam, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // On unmount: turn off the mic indicator and stop any in-flight recording.
  useEffect(() => () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try { rec.stop(); } catch { /* ignore */ }
    }
    if (meterRef.current) { meterRef.current.stop(); meterRef.current = null; }
    if (levelRafRef.current) { cancelAnimationFrame(levelRafRef.current); levelRafRef.current = 0; }
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const submit = useCallback(async (blob, durationSec, currentItem, ext, clientSilent) => {
    setPhase(PHASE.SUBMITTING);
    phaseRef.current = PHASE.SUBMITTING;
    try {
      const form = new FormData();
      form.append('set_id', currentItem.set_id);
      form.append('duration_sec', String(durationSec));
      form.append('audio', blob, `response.${ext}`);
      if (clientSilent) form.append('client_silent', 'true');
      const mockForm = searchParams.get('mock_form');
      if (mockForm) form.append('mock_form', mockForm);
      const res = await api.submitSpeaking(exam, form);
      // Backend now uploads-and-persists in ~200ms, grades in the background.
      // Redirect immediately to the review page which polls until the mark
      // lands — no more "Submitting…" spinner while the audio LLM churns.
      const s = res.data.submission;
      navigate(`/${exam}/submissions/${s.submission_id}/review`, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.submission_id) {
        navigate(`/${exam}/submissions/${detail.submission_id}/review`, { replace: true });
        return;
      }
      setError(typeof detail === 'string' ? detail : (detail?.message || 'Submission failed.'));
      setPhase(PHASE.ERROR);
      phaseRef.current = PHASE.ERROR;
    }
  }, [exam, searchParams, navigate]);

  const stopRecording = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  }, []);

  // Release the mic stream (turns off the browser's tab recording indicator).
  // Safe to call multiple times; idempotent.
  const releaseStream = useCallback(() => {
    if (meterRef.current) { meterRef.current.stop(); meterRef.current = null; }
    if (levelRafRef.current) { cancelAnimationFrame(levelRafRef.current); levelRafRef.current = 0; }
    const stream = streamRef.current;
    if (stream) {
      releaseMicStream(stream);
      streamRef.current = null;
    }
  }, []);

  // Mic acquisition runs on the START click, so the browser permission prompt
  // is the FIRST thing the student sees, before any countdown clock starts.
  // Mirrors the real exam's mic check at the front of the section. The stream
  // is kept warm for this one task; we release it on submit, error, or unmount.
  const acquireMic = useCallback(async () => {
    setMicError('');
    if (streamRef.current) return true;
    setMicBusy(true);
    try {
      const { stream, mime } = await acquireMicStream();
      streamRef.current = stream;
      mimeRef.current = mime;
      setMicBusy(false);
      return true;
    } catch (err) {
      setMicBusy(false);
      setMicError(err.message || 'Could not access the microphone.');
      return false;
    }
  }, []);

  const startRecording = useCallback((currentItem) => {
    const stream = streamRef.current;
    if (!stream) {
      setMicError('The microphone was disconnected. Click Start again to retry.');
      setPhase(PHASE.INTRO);
      phaseRef.current = PHASE.INTRO;
      return;
    }
    try {
      const { mime, ext } = mimeRef.current;
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      startedRef.current = Date.now();
      // Live volume analyser: powers the on-screen meter AND flags recordings
      // where the mic never picked up any speech (submitted as client_silent).
      meterRef.current = buildLevelMeter(stream);
      const pollLevel = () => {
        setLevel(meterRef.current?.getLevel() || 0);
        levelRafRef.current = requestAnimationFrame(pollLevel);
      };
      levelRafRef.current = requestAnimationFrame(pollLevel);
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, rec.mimeType ? { type: rec.mimeType } : {});
        const durationSec = (Date.now() - startedRef.current) / 1000;
        const clientSilent = !(meterRef.current?.didSpeak());
        releaseStream();
        submit(blob, durationSec, currentItem, ext, clientSilent);
      };
      rec.start();
      setPhase(PHASE.RECORD);
      phaseRef.current = PHASE.RECORD;
      setCountdown(currentItem.response_spec?.speak_sec || 60);
    } catch {
      releaseStream();
      setMicError('Could not start the recorder. Reload the page and try again.');
      setPhase(PHASE.INTRO);
      phaseRef.current = PHASE.INTRO;
    }
  }, [submit, releaseStream]);

  // countdown driver for PREP and RECORD
  useEffect(() => {
    if (phase !== PHASE.PREP && phase !== PHASE.RECORD) return undefined;
    const interval = setInterval(() => {
      setCountdown((s) => {
        if (s <= 1) {
          clearInterval(interval);
          // Snapshot the phase BEFORE branching. startRecording flips
          // phaseRef.current to RECORD synchronously, so a naive
          // second `if (phaseRef.current === RECORD)` here would fire
          // stopRecording() in the same tick and ship a 0-chunk blob
          // as "Empty recording". These branches are mutually exclusive.
          const before = phaseRef.current;
          if (before === PHASE.PREP) startRecording(item);
          else if (before === PHASE.RECORD) stopRecording();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, item, startRecording, stopRecording]);

  const spec = item?.response_spec || {};

  const runStart = useCallback(async () => {
    const ok = await acquireMic();
    if (!ok) return;
    setCountdown(spec.prep_sec || 30);
    setPhase(PHASE.PREP);
    phaseRef.current = PHASE.PREP;
  }, [acquireMic, spec.prep_sec]);

  const Header = () => (
    <header className="bg-blue-900 text-white">
      <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3">
          <Mic className="h-4 w-4 opacity-80" />
          <div>
            <p className="text-[11px] uppercase tracking-wider text-blue-200">{exam.toUpperCase()} practice • Speaking</p>
            <p className="text-sm font-semibold">{meta.title}</p>
          </div>
        </div>
        {(phase === PHASE.PREP || phase === PHASE.RECORD) && (
          <span
            className={cn(
              'rounded px-2.5 py-1 text-sm font-semibold tabular-nums',
              phase === PHASE.PREP ? 'bg-blue-950/60' : 'bg-red-600'
            )}
            data-testid="speaking-countdown"
          >
            {phase === PHASE.PREP ? 'Preparation: ' : 'Recording: '}
            {countdown}s
          </span>
        )}
      </div>
    </header>
  );

  if (phase === PHASE.ERROR) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-100">
        <p className="max-w-md text-center text-slate-600">{error}</p>
        <Link to={`/${exam}/practice`} className="text-sm font-medium text-blue-800 hover:underline">Back to practice</Link>
      </div>
    );
  }
  if (!item) return <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500">Preparing…</div>;

  return (
    <div className="min-h-screen bg-slate-100">
      <Header />
      <main className="mx-auto max-w-4xl px-6 py-10">
        {phase === PHASE.INTRO && (
          <div className="border border-slate-300 bg-white p-10 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-900">Instructions</p>
            <h1 className="mt-2 text-xl font-semibold text-slate-900">{meta.title}</h1>
            <ul className="mt-6 space-y-3 text-[15px] leading-6 text-slate-700">
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />When you click Start, your browser will ask permission to use the microphone. Allow it once and the test runs without interruption.</li>
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />You will have {spec.prep_sec} seconds to prepare. Recording starts the instant prep ends, no extra prompt.</li>
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />You will speak for up to {spec.speak_sec} seconds. Recording stops on its own.</li>
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />You record once. There is no retake, exactly like test day.</li>
            </ul>
            <div className="mt-10 flex items-center justify-between">
              <Link to={`/${exam}/practice`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="speaking-exit">
                <ArrowLeft className="h-4 w-4" /> Exit
              </Link>
              <Button
                size="lg"
                className="bg-blue-900 px-10 hover:bg-blue-800 disabled:opacity-60"
                data-testid="speaking-start"
                disabled={micBusy}
                onClick={runStart}
              >
                {micBusy ? 'Checking mic…' : 'START'}
              </Button>
            </div>
            {micError && (
              <div
                className="mt-6 border-l-2 border-red-500 bg-red-50/60 px-4 py-3 text-sm text-red-700"
                data-testid="speaking-mic-error"
              >
                <p>{micError}</p>
                <p className="mt-1 text-xs text-red-600/80">
                  If the prompt does not appear, click the lock or camera icon in your browser&apos;s address bar, allow the microphone, then try again.
                </p>
                <button
                  type="button"
                  data-testid="speaking-mic-retry"
                  disabled={micBusy}
                  onClick={runStart}
                  className="mt-3 inline-flex items-center rounded bg-blue-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
                >
                  {micBusy ? 'Checking mic…' : 'Try again'}
                </button>
              </div>
            )}
          </div>
        )}

        {(phase === PHASE.PREP || phase === PHASE.RECORD) && (
          <div className="space-y-6">
            <div className="border border-slate-300 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50 px-5 py-2.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Task</p>
              </div>
              <div className="px-6 py-5">
                <p className="text-[16px] leading-7 text-slate-800" data-testid="speaking-prompt">{spec.prompt_text}</p>
                {item?.stimulus?.type === 'scene' && (
                  <div className="mt-5 overflow-hidden border border-slate-300 bg-slate-50" data-testid="speaking-scene">
                    <SceneImage imageRef={item.stimulus.image_ref} scene={item.stimulus.scene} />
                  </div>
                )}
                {spec.options_cards && (
                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    {spec.options_cards.map((card) => (
                      <div key={card.title} className="border border-slate-300">
                        <p className="border-b border-slate-200 bg-slate-700 px-4 py-2 text-sm font-semibold text-white">{card.title}</p>
                        <ul className="space-y-1.5 px-4 py-3">
                          {card.details.map((d, i) => (
                            <li key={i} className="text-sm leading-6 text-slate-700">{d}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div
              className={cn(
                'flex items-center justify-center gap-3 border px-6 py-5 text-sm font-semibold',
                phase === PHASE.PREP
                  ? 'border-slate-300 bg-white text-slate-600'
                  : 'border-red-200 bg-red-50 text-red-700'
              )}
              data-testid="speaking-statusbox"
            >
              <span className={cn('h-2.5 w-2.5 rounded-full', phase === PHASE.PREP ? 'bg-slate-400' : 'animate-pulse bg-red-600')} />
              {phase === PHASE.PREP ? 'Preparing. Recording starts automatically when the countdown ends.' : 'Recording. Speak now.'}
            </div>
            {phase === PHASE.RECORD && (
              <div className="border border-slate-200 bg-white px-5 py-4" data-testid="speaking-mic-meter">
                <div className="flex items-center gap-3">
                  <Mic className="h-4 w-4 text-slate-500" />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mic input</p>
                  <p className="ml-auto text-xs text-slate-400">
                    {level > 0.035 ? 'Picking up your voice' : 'No sound detected — speak up'}
                  </p>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={cn(
                      'h-full transition-[width] duration-75',
                      level > 0.035 ? 'bg-emerald-500' : 'bg-slate-300'
                    )}
                    style={{ width: `${Math.min(100, Math.round(level * 800))}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {phase === PHASE.SUBMITTING && (
          <div className="flex flex-col items-center gap-3 py-24 text-slate-500">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-blue-900" />
            Uploading and transcribing your response…
          </div>
        )}
      </main>
    </div>
  );
}
